import {
  CountryCode,
  isSupportedCountry,
  isValidNumber,
  NationalNumber,
  format,
  parseNumber,
  ParsedNumber,
} from 'libphonenumber-js';
import { ValidationErrors } from '@angular/forms';

export const DEFAULT_COUNTRY_CODE: CountryCode = 'US';

export interface PhoneFields {
  nationalNumber: NationalNumber;
  isoCountryCode: CountryCode;
  extension?: string;
}

export const parsePhoneNumber = (phoneValue: string): PhoneFields => {
  const parsedNumber = parseNumber(phoneValue, {
    defaultCountry: DEFAULT_COUNTRY_CODE,
  }) as ParsedNumber;
  return {
    nationalNumber: parsedNumber.phone,
    isoCountryCode: parsedNumber.country,
    extension: parsedNumber.ext,
  };
};

export const formatAsRFC3966Phone = (parsedNumber: PhoneFields): string => {
  if (!parsedNumber.nationalNumber) {
    return '';
  }
  if (!/^\d+$/.test(parsedNumber.nationalNumber)) {
    const extension = parsedNumber.extension ? ` ext. ${parsedNumber.extension}` : '';
    return formatAsPhoneHref(`${parsedNumber.nationalNumber}${extension}`, parsedNumber.isoCountryCode);
  }
  const parsed = {
    country: toSupportedCountry(parsedNumber.isoCountryCode),
    phone: parsedNumber.nationalNumber,
    ext: parsedNumber.extension,
  } as ParsedNumber;
  let uri = '';
  try {
    uri = format(parsed, 'RFC3966');
  } catch (e) {
    console.warn('Failed formatting phone number as RFC 3966', parsed, e);
  }
  if (uri.startsWith('tel:')) {
    return uri;
  }
  const extension = parsedNumber.extension ? `;ext=${parsedNumber.extension}` : '';
  return `tel:${parsedNumber.nationalNumber}${extension}`;
};

const EXTENSION_PATTERN = /\s*(?:;\s*ext=|ext\.?|x|#)\s*(\d+)\s*$/i;

const toSupportedCountry = (country: string | null | undefined): CountryCode | undefined => {
  const code = country?.trim().toUpperCase();
  return code && isSupportedCountry(code) ? code : undefined;
};

/**
 * Builds an RFC 3966 `tel:` URI for an `href`, e.g. `tel:+13065551234;ext=56`.
 * A number without a leading `+` is read as `defaultCountry`. If that is empty or not a known country, it isn't guessed.
 * A number that can't be parsed falls back to `tel:` plus the number with whitespace removed, keeping a trailing
 * extension (`ext. 56`, `x56`, `#56`) as `;ext=56`. Text with no digits returns an empty string.
 */
export const formatAsPhoneHref = (phone: string | null | undefined, defaultCountry?: string | null): string => {
  const text = (phone ?? '').replace(/^tel:/i, '');
  const extension = EXTENSION_PATTERN.exec(text)?.[1];
  const unformatted = text.replace(EXTENSION_PATTERN, '').replace(/\s/g, '');
  if (!/\d/.test(unformatted)) {
    return '';
  }
  const country = toSupportedCountry(defaultCountry);
  try {
    const parsed = parseNumber(phone as string, country ? { defaultCountry: country } : {});
    if ('phone' in parsed && parsed.phone) {
      return formatAsRFC3966Phone({ nationalNumber: parsed.phone, isoCountryCode: parsed.country, extension: parsed.ext });
    }
  } catch (e) {
    console.warn('Failed parsing phone number', phone, e);
  }
  return `tel:${unformatted}${extension ? `;ext=${extension}` : ''}`;
};

export const validatePhone = (phone: NationalNumber, country: CountryCode): ValidationErrors | null => {
  let isInvalid = true;
  try {
    isInvalid = !isValidNumber(phone, country);
  } catch (e) {
    console.warn('Error validating phone number', phone, 'with country code', country, e);
  }

  if (isInvalid) {
    return {
      invalidPhoneNumber: true,
    };
  }
  
  const cannotParse = !Object.keys(
    parsePhoneNumber(formatAsRFC3966Phone({ nationalNumber: phone, isoCountryCode: country })),
  ).length;
  
  if (cannotParse) {
    return {
      invalidPhoneNumber: true,
    };
  }
  
  return null;
};

